import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import{
  Close as CloseIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOff as DisapproveIcon,
} from "@mui/icons-material";
import {
  format,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  getDay,
  getDaysInMonth,
  isToday,
  isWeekend,
} from "date-fns";
import {
  retrieveTravelCalendarEntriesByUserIdAndDateRangeApi,
  retrieveSharedSpecialOccasionsApi,
  updateApprovalStatusApi,
} from "../../../components/api/TravelCalendarApiService";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_ABBR = ["M", "T", "W", "T", "F", "S", "S"];

// Color helpers
const getEntryColor = (entry) => {
  if (!entry) return null;
  if (entry.entryType === "ACTUAL") {
    if (entry.approvalStatus === "APPROVED") return "#4caf50";
    if (entry.approvalStatus === "NOT_APPROVED") return "#f44336";
    return "#ff9800"; // WAITING_FOR_APPROVAL
  }
  // TEMPLATE
  return "#42a5f5";
};

const getEntryTooltip = (entry) => {
  if (!entry) return "";
  const parts = [];
  if (entry.entryType) parts.push(entry.entryType);
  if (entry.destination) parts.push(entry.destination);
  if (entry.entryType === "ACTUAL" && entry.approvalStatus) parts.push(entry.approvalStatus.replace(/_/g, " "));
  if (entry.flight) parts.push(`Flight: ${entry.flight}`);
  if (entry.comment) parts.push(entry.comment);
  return parts.join(" · ");
};

// --- Month mini-grid component ---
const MonthGrid = React.memo(({ year, monthIndex, entryMap, sharedOccasionMap, onDayClick, onMonthClick, hoveredCategory }) => {
  const firstDay = new Date(year, monthIndex, 1);
  const totalDays = getDaysInMonth(firstDay);
  const startDow = (getDay(firstDay) + 6) % 7; // 0=Mon

  // Build 6-week grid (max rows needed)
  const weeks = [];
  let day = 1;
  for (let week = 0; week < 6; week++) {
    const cells = [];
    for (let dow = 0; dow < 7; dow++) {
      if ((week === 0 && dow < startDow) || day > totalDays) {
        cells.push(null);
      } else {
        cells.push(day);
        day++;
      }
    }
    weeks.push(cells);
    if (day > totalDays) break;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="subtitle2"
        onClick={() => onMonthClick && onMonthClick(monthIndex)}
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          color: "#1E2685",
          mb: 0.5,
          fontSize: "0.8rem",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {MONTH_NAMES[monthIndex]}
      </Typography>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {DAY_ABBR.map((d, i) => (
              <TableCell
                key={i}
                align="center"
                sx={{
                  p: 0,
                  fontSize: "0.6rem",
                  fontWeight: "bold",
                  color: i === 5 || i === 6 ? "#c62828" : "#666",
                  borderBottom: "1px solid #e0e0e0",
                  lineHeight: 1.6,
                }}
              >
                {d}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {weeks.map((week, wi) => (
            <TableRow key={wi}>
              {week.map((d, di) => {
                if (d === null) {
                  return <TableCell key={di} sx={{ p: 0, border: "none" }} />;
                }
                const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const dayEntries = entryMap[dateStr] || [];
                const entry = dayEntries[0] || null; // first for color/tooltip
                const sharedOcc = sharedOccasionMap[dateStr];
                const hasSharedOcc = sharedOcc && sharedOcc.length > 0;
                const color = getEntryColor(entry);
                const dateObj = new Date(year, monthIndex, d);
                const today = isToday(dateObj);
                const weekend = isWeekend(dateObj);
                const isCategoryMatch = hoveredCategory && dayEntries.some((e) => e.category === hoveredCategory);

                return (
                  <TableCell
                    key={di}
                    align="center"
                    onClick={() => (dayEntries.length > 0 || hasSharedOcc) && onDayClick(dayEntries, dateObj, sharedOcc)}
                    sx={{
                      p: 0,
                      border: "none",
                      cursor: dayEntries.length > 0 || hasSharedOcc ? "pointer" : "default",
                      position: "relative",
                    }}
                  >
                    <Tooltip
                      title={
                        dayEntries.length > 1
                          ? dayEntries.map((e) => `${getEntryTooltip(e)} (${e.halfDayType === "FIRST" ? "1st half" : "2nd half"})`).join(" | ")
                          : entry && entry.halfDayType && entry.halfDayType !== "NONE"
                          ? `${getEntryTooltip(entry)} (${entry.halfDayType === "FIRST" ? 'First Half' : 'Second Half'})`
                          : entry
                          ? getEntryTooltip(entry)
                          : hasSharedOcc
                          ? `🎉 ${sharedOcc.join(" | ")}`
                          : ""
                      }
                      arrow
                      disableHoverListener={!entry && !hasSharedOcc}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          lineHeight: "22px",
                          borderRadius: "50%",
                          fontSize: "0.65rem",
                          fontWeight: today ? "bold" : "normal",
                          mx: "auto",
                          my: "1px",
                          backgroundColor: color || (today ? "#fff9c4" : "transparent"),
                          color: color
                            ? "#fff"
                            : weekend
                            ? "#c62828"
                            : today
                            ? "#1E2685"
                            : "#333",
                          border: today ? "2px solid #1E2685" : hasSharedOcc ? "2px solid #ce93d8" : "none",
                          outline: isCategoryMatch ? `3px solid ${CATEGORY_COLORS[hoveredCategory]}` : "none",
                          outlineOffset: "2px",
                          boxShadow: isCategoryMatch ? `0 0 6px 2px ${CATEGORY_COLORS[hoveredCategory]}88` : "none",
                          transition: "transform 0.1s, outline 0.1s, box-shadow 0.1s",
                          "&:hover": entry
                            ? { transform: "scale(1.3)", boxShadow: 2 }
                            : {},
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <span style={{ display: 'inline-block', minWidth: 12, position: 'relative' }}>
                          {d}
                          {entry && entry.halfDayType && entry.halfDayType !== "NONE" && (
                            <span
                              style={{
                                position: 'absolute',
                                top: 1,
                                right: 0,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: entry.halfDayType === "FIRST" ? '#fff8b7' : '#104b66',
                                border: '1px solid #fff',
                                boxShadow: '0 0 1px #888',
                                display: 'inline-block',
                              }}
                            />
                          )}
                        </span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
});

// --- Legend ---
const Legend = () => (
  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center", mt: 1, mb: 2 }}>
    {[
      { color: "#42a5f5", label: "Template" },
      { color: "#ff9800", label: "Actual (Waiting)" },
      { color: "#4caf50", label: "Actual (Approved)" },
      { color: "#f44336", label: "Actual (Not Approved)" },
      { border: "2px solid #ce93d8", label: "Shared Occasion" },
    ].map((item, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: item.color || "transparent",
            border: item.border || "none",
          }}
        />
        <Typography variant="caption" sx={{ color: "#555" }}>
          {item.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

// --- Summary stats ---
const CATEGORY_LABELS = {
  ANNUAL_LEAVE: "Annual Leave",
  BUSINESS_TRIP: "Business Trip",
  NATIONAL_HOLIDAY: "National Holiday",
  COMPENSATION: "Compensation",
  OTHER: "Other",
};

const CATEGORY_COLORS = {
  ANNUAL_LEAVE: "#7b1fa2",
  BUSINESS_TRIP: "#1565c0",
  NATIONAL_HOLIDAY: "#2e7d32",
  COMPENSATION: "#e65100",
  OTHER: "#546e7a",
};

const YearlySummary = ({ entries, hoveredCategory, onCategoryHover }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const stats = useMemo(() => {
      // Group entries by date so we can detect 2-record days
      const countPerDate = {};
      entries.forEach((e) => {
        const dk = e.entryDate ? e.entryDate.substring(0, 10) : null;
        if (dk) countPerDate[dk] = (countPerDate[dk] || 0) + 1;
      });

      // If a date has 2 entries they share the day equally (0.5 each),
      // otherwise fall back to the stored halfDayType
      const getDayValue = (e) => {
        const dk = e.entryDate ? e.entryDate.substring(0, 10) : null;
        if (dk && countPerDate[dk] > 1) return 0.5;
        if (e.halfDayType === "FIRST" || e.halfDayType === "LAST") return 0.5;
        return 1;
      };
      const templateCount = entries.filter((e) => e.entryType === "TEMPLATE").reduce((sum, e) => sum + getDayValue(e), 0);
      const actualCount = entries.filter((e) => e.entryType === "ACTUAL").reduce((sum, e) => sum + getDayValue(e), 0);
      const approvedCount = entries.filter(
        (e) => e.entryType === "ACTUAL" && e.approvalStatus === "APPROVED"
      ).reduce((sum, e) => sum + getDayValue(e), 0);
      const waitingCount = entries.filter(
        (e) => e.entryType === "ACTUAL" && e.approvalStatus === "WAITING_FOR_APPROVAL"
      ).reduce((sum, e) => sum + getDayValue(e), 0);
      const notApprovedCount = entries.filter(
        (e) => e.entryType === "ACTUAL" && e.approvalStatus === "NOT_APPROVED"
      ).reduce((sum, e) => sum + getDayValue(e), 0);
      const total = entries.reduce((sum, e) => sum + getDayValue(e), 0);
    const inEuCount = entries.filter((e) => e.inEu).length;
    const inSinCount = entries.filter((e) => e.inSin).length;

    // Category breakdown — only ACTUAL & APPROVED entries
    const categoryMap = {};
    entries
      .filter((e) => e.entryType === "ACTUAL" && e.approvalStatus === "APPROVED")
      .forEach((e) => {
        if (e.category) {
          categoryMap[e.category] = (categoryMap[e.category] || 0) + getDayValue(e);
        }
      });

    return { templateCount, actualCount, approvedCount, waitingCount, notApprovedCount, inEuCount, inSinCount, total, categoryMap };
  }, [entries]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#1E2685", mb: 1 }}>
        Yearly Summary
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        <Chip label={`Total: ${stats.total}`} size="small" sx={{ fontWeight: "bold" }} />
        <Chip label={`Template: ${stats.templateCount}`} size="small" sx={{ backgroundColor: "#42a5f5", color: "#fff" }} />
        <Chip label={`Actual: ${stats.actualCount}`} size="small" sx={{ backgroundColor: "#ff9800", color: "#fff" }} />
        <Chip label={`Approved: ${stats.approvedCount}`} size="small" sx={{ backgroundColor: "#4caf50", color: "#fff" }} />
        <Chip label={`Waiting: ${stats.waitingCount}`} size="small" sx={{ backgroundColor: "#fff3e0", color: "#e65100" }} />
        <Chip label={`Not Approved: ${stats.notApprovedCount}`} size="small" sx={{ backgroundColor: "#f44336", color: "#fff" }} />
        <Chip label={`In EU: ${stats.inEuCount}`} size="small" variant="outlined" />
        <Chip label={`In Singapore: ${stats.inSinCount}`} size="small" variant="outlined" />
      </Box>
      {Object.keys(stats.categoryMap).length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 0.5 }}>
            By Category (Actual &amp; Approved):
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {Object.entries(stats.categoryMap).map(([cat, count]) => (
              <Chip
                key={cat}
                label={`${CATEGORY_LABELS[cat] || cat}: ${count}`}
                size="small"
                onClick={() => setSelectedCategory(cat)}
                onMouseEnter={() => onCategoryHover(cat)}
                onMouseLeave={() => onCategoryHover(null)}
                sx={{
                  backgroundColor: CATEGORY_COLORS[cat] || "#90a4ae",
                  color: "#fff",
                  cursor: "pointer",
                  outline: hoveredCategory === cat ? `2px solid #fff` : "none",
                  boxShadow: hoveredCategory === cat ? `0 0 0 4px ${CATEGORY_COLORS[cat] || "#90a4ae"}` : "none",
                  transition: "box-shadow 0.15s",
                  "&:hover": { opacity: 0.9 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <CategoryDetailDialog
        open={Boolean(selectedCategory)}
        category={selectedCategory}
        entries={entries}
        onClose={() => setSelectedCategory(null)}
      />
    </Paper>
  );
};

// --- Category detail dialog ---
const CategoryDetailDialog = ({ open, category, entries, onClose }) => {
  const filtered = useMemo(() => {
    if (!category) return [];
    return entries
      .filter((e) => e.category === category)
      .sort((a, b) => (a.entryDate > b.entryDate ? 1 : -1));
  }, [category, entries]);

  const extractTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const str = String(dateTimeStr);
    const match = str.match(/T(\d{2}:\d{2})/);
    if (match) return match[1];
    const timeOnly = str.match(/^(\d{2}:\d{2})/);
    if (timeOnly) return timeOnly[1];
    return "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try { return format(new Date(dateStr.substring(0, 10) + "T00:00:00"), "dd MMM yyyy, EEE"); }
    catch { return dateStr; }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={CATEGORY_LABELS[category] || category}
            size="small"
            sx={{ backgroundColor: CATEGORY_COLORS[category] || "#90a4ae", color: "#fff", fontWeight: "bold" }}
          />
          <Typography variant="body2" sx={{ color: "#666" }}>
            {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                {["Date", "Destination", "Flight", "ETD", "ETA", "Comment", "Others", "Type", "Status"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: "bold", fontSize: "0.75rem", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.78rem" }}>{formatDate(e.entryDate)}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem" }}>{e.destination || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem" }}>{e.flight || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem" }}>{extractTime(e.etd) || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem" }}>{extractTime(e.eta) || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.78rem", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={e.comment || ""} arrow disableHoverListener={!e.comment}>
                      <span>{e.comment || "—"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.78rem", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={e.others || ""} arrow disableHoverListener={!e.others}>
                      <span>{e.others || "—"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={e.entryType === "TEMPLATE" ? "Template" : "Actual"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.65rem", height: 18, borderColor: e.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0", color: e.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0" }}
                    />
                  </TableCell>
                  <TableCell>
                    {e.entryType === "ACTUAL" && (
                      <Chip
                        label={e.approvalStatus === "APPROVED" ? "Approved" : e.approvalStatus === "NOT_APPROVED" ? "Not Approved" : "Waiting"}
                        size="small"
                        sx={{
                          fontSize: "0.65rem",
                          height: 18,
                          backgroundColor: e.approvalStatus === "APPROVED" ? "#e8f5e9" : e.approvalStatus === "NOT_APPROVED" ? "#ffebee" : "#fff8e1",
                          color: e.approvalStatus === "APPROVED" ? "#2e7d32" : e.approvalStatus === "NOT_APPROVED" ? "#c62828" : "#f57f17",
                          fontWeight: "bold",
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: "#999", py: 3 }}>No entries found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Inspect dialog for a single day (reused from yearly view) ---
const DayInspectDialog = ({ open, entries, date, sharedOccasion, isOwnCalendar, isManager, onApprove, onDisapprove, onClose }) => {
  const [disapproveModeId, setDisapproveModeId] = useState(null);
  const [disapproveNote, setDisapproveNote] = useState("");

  const entryList = entries || [];
  const hasContent = entryList.length > 0 || (sharedOccasion && sharedOccasion.length > 0);
  if (!hasContent) return null;

  const extractTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const str = String(dateTimeStr);
    const match = str.match(/T(\d{2}:\d{2})/);
    if (match) return match[1];
    const timeOnly = str.match(/^(\d{2}:\d{2})/);
    if (timeOnly) return timeOnly[1];
    return "";
  };

  // For a single entry, build one flat rows table (original behaviour).
  // For two half-day entries, show shared fields once then per-entry flight+status sections.
  const isHalfDay = entryList.length > 1;

  // Helper: resolve a field from the first entry that has it non-empty, else second
  const shared = (field) => {
    for (const e of entryList) {
      const v = e[field];
      if (v !== null && v !== undefined && v !== "" && v !== false) return v;
    }
    return entryList[0]?.[field]; // fall back to first (could be falsy)
  };

  const renderFlightRows = (entry) => {
    const flights = entry.flights && entry.flights.length > 0
      ? entry.flights
      : [{ destination: entry.destination, flight: entry.flight, etd: entry.etd, eta: entry.eta }];
    return flights.map((f, i) => (
      <React.Fragment key={i}>
        {flights.length > 1 && (
          <TableRow>
            <TableCell colSpan={2} sx={{ pt: i === 0 ? 0.5 : 1, pb: 0, fontWeight: "bold", color: "#1565c0", fontSize: "0.75rem" }}>
              Flight {i + 1}
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", color: "#333", width: 140, py: 0.5 }}>Destination</TableCell>
          <TableCell sx={{ py: 0.5 }}>{f.destination || "—"}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Flight</TableCell>
          <TableCell sx={{ py: 0.5 }}>{f.flight || "—"}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>ETD</TableCell>
          <TableCell sx={{ py: 0.5 }}>{extractTime(f.etd) || "—"}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>ETA</TableCell>
          <TableCell sx={{ py: 0.5 }}>{extractTime(f.eta) || "—"}</TableCell>
        </TableRow>
      </React.Fragment>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          Entry Details
          {date && (
            <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
              {format(date, "dd MMMM yyyy, EEEE")}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>

        {/* ── Single entry: full table ─────────────────────────────── */}
        {!isHalfDay && entryList.map((entry, idx) => {
          const canApprove = isManager && entry.entryType === "ACTUAL";
          const isDisapprovingThis = disapproveModeId === entry.id;
          return (
            <Box key={entry.id} sx={{ mt: 1 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", width: 140, py: 0.5 }}>Type</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{entry.entryType || "—"}</TableCell>
                  </TableRow>
                  {entry.category && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Category</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{CATEGORY_LABELS[entry.category] || entry.category}</TableCell>
                    </TableRow>
                  )}
                  {renderFlightRows(entry)}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Comment</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{entry.comment || "—"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Others</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{entry.others || "—"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>In EU</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{entry.inEu ? "Yes" : "No"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>In Singapore</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{entry.inSin ? "Yes" : "No"}</TableCell>
                  </TableRow>
                  {entry.specialOccasion && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Special Occasion</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{entry.specialOccasion}</TableCell>
                    </TableRow>
                  )}
                  {entry.entryType === "ACTUAL" && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Status</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{entry.approvalStatus?.replace(/_/g, " ") || "—"}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {canApprove && !isDisapprovingThis && (
                <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                  {entry.approvalStatus !== "APPROVED" && (
                    <Button variant="contained" size="small" color="success" startIcon={<ApproveIcon />} onClick={() => onApprove(entry.id)}>
                      Approve
                    </Button>
                  )}
                  {entry.approvalStatus !== "NOT_APPROVED" && (
                    <Button variant="contained" size="small" color="error" startIcon={<DisapproveIcon />} onClick={() => { setDisapproveModeId(entry.id); setDisapproveNote(""); }}>
                      Disapprove
                    </Button>
                  )}
                </Box>
              )}
              {canApprove && isDisapprovingThis && (
                <Box sx={{ mt: 1 }}>
                  <TextField label="Disapproval reason" fullWidth size="small" multiline rows={2} value={disapproveNote} onChange={(e) => setDisapproveNote(e.target.value)} sx={{ mb: 1 }} />
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button size="small" onClick={() => { setDisapproveModeId(null); setDisapproveNote(""); }}>Cancel</Button>
                    <Button variant="contained" size="small" color="error" onClick={() => { onDisapprove(entry.id, disapproveNote); setDisapproveModeId(null); setDisapproveNote(""); }}>
                      Confirm Disapprove
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        {/* ── Two half-day entries: shared fields once + per-entry sections ── */}
        {isHalfDay && (
          <>
            {/* Shared fields */}
            <Box sx={{ mt: 1 }}>
              <Table size="small">
                <TableBody>
                  {shared("category") && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#333", width: 140, py: 0.5 }}>Category</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{CATEGORY_LABELS[shared("category")] || shared("category")}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Comment</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{shared("comment") || "—"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Others</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{shared("others") || "—"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>In EU</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{(shared("inEu") || entryList[0]?.inEu) ? "Yes" : "No"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>In Singapore</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{(shared("inSin") || entryList[0]?.inSin) ? "Yes" : "No"}</TableCell>
                  </TableRow>
                  {shared("specialOccasion") && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Special Occasion</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{shared("specialOccasion")}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            {/* Per-entry (flights + type + status) */}
            {entryList.map((entry, idx) => {
              const canApprove = isManager && entry.entryType === "ACTUAL";
              const isDisapprovingThis = disapproveModeId === entry.id;
              const halfTitle = entry.halfDayType === "FIRST" ? "1st Half" : entry.halfDayType === "LAST" ? "2nd Half" : `Part ${idx + 1}`;
              return (
                <Box key={entry.id} sx={{ mt: 2, border: "1px solid #e0e0e0", borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: "bold", color: "#555", mb: 0.5, display: "block" }}>
                    {halfTitle}
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", color: "#333", width: 140, py: 0.5 }}>Type</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{entry.entryType || "—"}</TableCell>
                      </TableRow>
                      {renderFlightRows(entry)}
                      {entry.entryType === "ACTUAL" && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", color: "#333", py: 0.5 }}>Status</TableCell>
                          <TableCell sx={{ py: 0.5 }}>{entry.approvalStatus?.replace(/_/g, " ") || "—"}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {canApprove && !isDisapprovingThis && (
                    <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                      {entry.approvalStatus !== "APPROVED" && (
                        <Button variant="contained" size="small" color="success" startIcon={<ApproveIcon />} onClick={() => onApprove(entry.id)}>Approve</Button>
                      )}
                      {entry.approvalStatus !== "NOT_APPROVED" && (
                        <Button variant="contained" size="small" color="error" startIcon={<DisapproveIcon />} onClick={() => { setDisapproveModeId(entry.id); setDisapproveNote(""); }}>Disapprove</Button>
                      )}
                    </Box>
                  )}
                  {canApprove && isDisapprovingThis && (
                    <Box sx={{ mt: 1 }}>
                      <TextField label="Disapproval reason" fullWidth size="small" multiline rows={2} value={disapproveNote} onChange={(e) => setDisapproveNote(e.target.value)} sx={{ mb: 1 }} />
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button size="small" onClick={() => { setDisapproveModeId(null); setDisapproveNote(""); }}>Cancel</Button>
                        <Button variant="contained" size="small" color="error" onClick={() => { onDisapprove(entry.id, disapproveNote); setDisapproveModeId(null); setDisapproveNote(""); }}>
                          Confirm Disapprove
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}

        {/* Shared occasions */}
        {sharedOccasion && sharedOccasion.length > 0 && (
          <Box sx={{ mt: entryList.length > 0 ? 2 : 1 }}>
            <Table size="small">
              <TableBody>
                {sharedOccasion.map((occ, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: "bold", color: "#6a1b9a", width: 140, py: 0.5 }}>Shared Occasion</TableCell>
                    <TableCell sx={{ py: 0.5, color: "#6a1b9a", fontStyle: "italic" }}>{occ}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Main Yearly Calendar ---
const YearlyCalendar = ({ year, userId, userName, isOwnCalendar, isManager, yearlyRef, onMonthClick }) => {
  const [entries, setEntries] = useState([]);
  const [sharedOccasions, setSharedOccasions] = useState([]);
  const [inspectEntries, setInspectEntries] = useState([]);
  const [inspectDate, setInspectDate] = useState(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  useEffect(() => {
    if (!userId) return;
    loadYearlyData();
  }, [userId, year]);

  const loadYearlyData = useCallback(() => {
    if (!userId) return;
    const start = format(startOfYear(new Date(year, 0, 1)), "yyyy-MM-dd");
    const end = format(endOfYear(new Date(year, 0, 1)), "yyyy-MM-dd");

    retrieveTravelCalendarEntriesByUserIdAndDateRangeApi(userId, start, end)
      .then((res) => setEntries(res?.data || []))
      .catch((err) => console.error("Error loading yearly entries:", err));

    retrieveSharedSpecialOccasionsApi(start, end)
      .then((res) => setSharedOccasions(res?.data || []))
      .catch((err) => console.error("Error loading shared occasions:", err));
  }, [userId, year]);

  const entryMap = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (e.entryDate) {
        const key = e.entryDate.substring(0, 10);
        if (!map[key]) map[key] = [];
        map[key].push(e);
      }
    });
    return map;
  }, [entries]);

  const sharedOccasionMap = useMemo(() => {
    const map = {};
    sharedOccasions.forEach((e) => {
      if (e.entryDate && e.specialOccasion) {
        const key = e.entryDate.substring(0, 10);
        const name = e.user?.name || e.user?.username || "";
        if (!map[key]) map[key] = [];
        map[key].push(`${e.specialOccasion} (${name})`);
      }
    });
    return map;
  }, [sharedOccasions]);

  const [inspectSharedOccasion, setInspectSharedOccasion] = useState(null);

  const handleDayClick = useCallback((dayEntries, date, sharedOcc) => {
    setInspectEntries(Array.isArray(dayEntries) ? dayEntries : (dayEntries ? [dayEntries] : []));
    setInspectDate(date);
    setInspectSharedOccasion(sharedOcc || null);
    setInspectOpen(true);
  }, []);

  const handleApprove = useCallback((entryId) => {
    updateApprovalStatusApi(entryId, "APPROVED")
      .then(() => {
        setInspectOpen(false);
        loadYearlyData();
      })
      .catch((err) => console.error("Failed to approve:", err));
  }, [loadYearlyData]);

  const handleDisapprove = useCallback((entryId, note) => {
    updateApprovalStatusApi(entryId, "NOT_APPROVED", note)
      .then(() => {
        setInspectOpen(false);
        loadYearlyData();
      })
      .catch((err) => console.error("Failed to disapprove:", err));
  }, [loadYearlyData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Yearly Calendar
        </Typography>
      </Box>
      <Box ref={yearlyRef}>
        <YearlySummary entries={entries} hoveredCategory={hoveredCategory} onCategoryHover={setHoveredCategory} />
        <Legend />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(6, 1fr)",
            },
            gap: 2,
          }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <Paper key={i} sx={{ p: 1 }} variant="outlined">
              <MonthGrid
                year={year}
                monthIndex={i}
                entryMap={entryMap}
                sharedOccasionMap={sharedOccasionMap}
                onDayClick={handleDayClick}
                onMonthClick={onMonthClick}
                hoveredCategory={hoveredCategory}
              />
            </Paper>
          ))}
        </Box>
      </Box>
      <DayInspectDialog
        open={inspectOpen}
        entries={inspectEntries}
        date={inspectDate}
        sharedOccasion={inspectSharedOccasion}
        isOwnCalendar={isOwnCalendar}
        isManager={isManager}
        onApprove={handleApprove}
        onDisapprove={handleDisapprove}
        onClose={() => setInspectOpen(false)}
      />
    </Box>
  );
};

export default YearlyCalendar;
